import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Input, Tooltip } from "antd";
import clsx from "clsx";
import { UseFormSetValue, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { normalizeFilestorePath } from "@entities/document";
import { DocumentFileRow } from "@entities/journal";
import type { SectionFourDocTypeEnum } from "../../../../../../model/types";
import { InfoDropdown } from "../info-dropdown/info-dropdown";
import { SectionDocumentModal } from "../section-document-modal/section-document-modal";

interface SectionDocumentItemProps {
  title: string;
  docType: SectionFourDocTypeEnum;
  setValue?: UseFormSetValue<any>;
  isInfo?: boolean;
  control: any;
  isCollapsed?: boolean;
}

const SectionDocumentItem = (
  props: SectionDocumentItemProps,
  ref: Ref<unknown> | undefined,
) => {
  const { title, docType, setValue, isInfo, control, isCollapsed } = props;

  const [isModalVisible, setIsModalVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalVisible(true),
  }));

  const allDocs = useWatch({ control, name: "docs" }) || [];
  const documents = allDocs.filter((doc: any) => doc.docType === docType);

  const updateFormDocs = (updatedDocsForThisType: any[]) => {
    if (!setValue) return;

    const withoutCurrentType = allDocs.filter(
      (doc: any) => doc.docType !== docType,
    );

    const uniqueDocs = updatedDocsForThisType.filter((doc, index, self) =>
      index === self.findIndex((d) =>
        d.docType === doc.docType &&
        (d.docId === doc.docId || d.id === doc.id),
      ),
    );

    const merged = [...withoutCurrentType, ...uniqueDocs];

    setValue("docs", merged, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleDelete = (docLocal: any) => {
    const updated = documents.filter((doc: any) =>
      !(doc.docType === docLocal.docType &&
        (doc.docId === docLocal.docId || doc.id === docLocal.id)),
    );
    const updatedDocsForThisType = updated.map((doc: any) => ({
      ...doc,
      docId: doc.docId || doc.id,
      docType,
    }));
    updateFormDocs(updatedDocsForThisType);
  };

  return (
    <div
      className={clsx(
        "document-container",
        isCollapsed && "document-container--collapsed",
      )}
    >
      <div className="document-description">{title}</div>

      {documents.map((doc: any) => {
        const uniqueKey = `${docType}_${doc.docId || doc.id}`;
        const formIndex = allDocs.findIndex((d: any) =>
          d.docType === doc.docType &&
          (d.docId === doc.docId || d.id === doc.id),
        );

        return (
          <div className="document-item" key={uniqueKey}>
            <div className="document-name">
              <div className="document-name-container">
                <DocumentFileRow
                  className="document-name-container-title-row"
                  linkClassName="document-name-container-title"
                  fileName={doc?.title || doc?.docTitle}
                  documentUrl={
                    doc?.originalData?.documentUrl || doc?.documentUrl
                      ? normalizeFilestorePath(
                          doc?.originalData?.documentUrl ||
                            doc?.documentUrl ||
                            "",
                        )
                      : undefined
                  }
                  actions={
                    <>
                      <Tooltip title="Информация о документе">
                        <div className="journal-document-info-trigger">
                          <InfoDropdown docType={docType} docId={doc?.docId || doc?.id} />
                        </div>
                      </Tooltip>
                      {!isInfo && (
                        <Tooltip title="Удалить">
                          <DeleteOutlined onClick={() => handleDelete(doc)} />
                        </Tooltip>
                      )}
                    </>
                  }
                />

                <div className="document-name-container-description">
                  {doc?.originalData?.searchField}
                </div>

                {!isInfo ? (
                  <FormItem
                    control={control}
                    name={`docs.${formIndex}.docInfo`}
                    style={{ width: "99%", marginBottom: 0 }}
                  >
                    <Input
                      style={{ width: "100%" }}
                      placeholder="Описание документа"
                      maxLength={255}
                      showCount
                    />
                  </FormItem>
                ) : (
                  doc.docInfo || ""
                )}
              </div>
            </div>
          </div>
        );
      })}

      {!isInfo && (
        <div className="document-add-button">
          <Button
            type="dashed"
            onClick={() => setIsModalVisible(true)}
            style={{ backgroundColor: "rgba(234, 238, 248, 1)" }}
          >
            Добавить
          </Button>
        </div>
      )}

      <SectionDocumentModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        docType={docType}
        onConfirm={(selectedDocs) => {
          const newDocs = selectedDocs
            .filter((doc: any) => !documents.find((d: any) =>
              d.docType === docType &&
              (d.docId === doc.docId || d.id === doc.key),
            ))
            .map((doc: any) => ({
              ...doc,
              id: doc.key,
              docId: doc.docId || doc.key,
              docType,
            }));

          updateFormDocs([...documents, ...newDocs]);
          setIsModalVisible(false);
        }}
        key={documents.length}
      />
    </div>
  );
};

export default forwardRef(SectionDocumentItem);
